import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa6';
import SafeIcon from '../components/common/SafeIcon';
import useSEO from '../hooks/useSEO';

const { FiMail, FiPhone, FiMapPin, FiSend, FiClock, FiGlobe, FiMessageSquare, FiNavigation, FiCheckCircle, FiAlertCircle, FiFacebook, FiInstagram } = FiIcons;
const { FaThreads } = FaIcons;

const Contact = () => {
  useSEO({
    title: 'Contact Us - Get a Quote for Water Treatment Products',
    description: 'Contact Minipore for water treatment product inquiries, technical support, and custom solutions. Located in Shanghai, China. Email: johnny@minipore.com | WhatsApp: +86 131 2227 7752',
    keywords: 'contact Minipore, water treatment quote, RO membrane supplier contact, filter housing inquiry, Shanghai water treatment',
    url: '/contact',
  });

  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', company: '', phone: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await fetch('https://cloud.activepieces.com/api/v1/webhooks/CsaDiL60CbEkPxu5xwYNG', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, timestamp: new Date().toISOString(), source: 'totalfilt.com' }),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', company: '', phone: '', subject: '', message: '' });
      } else throw new Error('Failed');
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: FiMapPin, title: 'Address', content: '2755 Baoan Hwy, Jia Ding Qu, Shang Hai Shi, 201907', color: 'bg-blue-100 text-blue-600' },
    { icon: FiMail, title: 'Email', content: 'johnny@minipore.com', color: 'bg-green-100 text-green-600' },
    { icon: FiPhone, title: 'WhatsApp', content: '+86 131 2227 7752', color: 'bg-purple-100 text-purple-600' },
    { icon: FiGlobe, title: 'Website', content: 'http://www.minipore.com', color: 'bg-orange-100 text-orange-600' },
  ];

  const socialLinks = [
    { icon: FiFacebook, name: 'Facebook', url: 'https://www.facebook.com/huamo.official', bgColor: 'bg-[#1877F2]', hoverColor: 'hover:bg-[#0d65d9]' },
    { icon: FiInstagram, name: 'Instagram', url: 'https://www.instagram.com/huamo.official/', bgColor: 'bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]', hoverColor: 'hover:opacity-90' },
    { icon: FaThreads, name: 'Threads', url: 'https://www.threads.com/@huamo.official', bgColor: 'bg-black', hoverColor: 'hover:bg-gray-800' },
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '09:00 - 17:30' },
    { day: 'Saturday', hours: '09:30 - 16:00' },
    { day: 'Sunday', hours: 'Closed' },
  ];

  return (
    <div className="pt-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get in Touch</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">Ready to discuss your water treatment needs? Contact us today for professional consultation and customized solutions.</p>
        </motion.div>

        <section className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div key={info.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className={`p-3 rounded-full w-fit mb-4 ${info.color}`}><SafeIcon icon={info.icon} className="h-6 w-6" /></div>
                <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-gray-600 text-sm whitespace-pre-line">{info.content}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <motion.section initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="mb-16 flex flex-wrap justify-center gap-4">
          {socialLinks.map((social) => (
            <motion.a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" whileHover={{ y: -5 }} className={`flex items-center space-x-3 px-6 py-3 rounded-xl text-white font-semibold shadow-md transition-all ${social.bgColor} ${social.hoverColor}`}>
              <SafeIcon icon={social.icon} className="text-xl" />
              <span>{social.name}</span>
            </motion.a>
          ))}
        </motion.section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="bg-white p-8 rounded-2xl shadow-lg">
            <div className="flex items-center mb-6">
              <div className="bg-blue-100 p-3 rounded-full mr-4"><SafeIcon icon={FiMessageSquare} className="h-6 w-6 text-blue-600" /></div>
              <h2 className="text-2xl font-bold text-gray-900">Send us a Message</h2>
            </div>
            {submitStatus === 'success' && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-600" />
                <p className="text-green-800 font-medium">Thank you! We will get back to you soon.</p>
              </motion.div>
            )}
            {submitStatus === 'error' && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-600" />
                <p className="text-red-800 font-medium">Error sending message. Please try again or contact us directly.</p>
              </motion.div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100" placeholder="John" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100" placeholder="john@example.com" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100" placeholder="Your Company" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100" placeholder="+1 (555) 123-4567" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                <select name="subject" value={formData.subject} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100">
                  <option value="">Select a subject</option>
                  <option value="product-inquiry">Product Inquiry</option>
                  <option value="technical-support">Technical Support</option>
                  <option value="partnership">Partnership</option>
                  <option value="quotation">Request Quotation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} disabled={isSubmitting} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100" placeholder="Please describe your water treatment needs..."></textarea>
              </div>
              <motion.button whileHover={{ scale: isSubmitting ? 1 : 1.02 }} whileTap={{ scale: isSubmitting ? 1 : 0.98 }} type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2">
                {isSubmitting ? (<><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div><span>Sending...</span></>) : (<><span>Send Message</span><SafeIcon icon={FiSend} className="h-5 w-5" /></>)}
              </motion.button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="space-y-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center mb-6">
                <div className="bg-green-100 p-3 rounded-full mr-4"><SafeIcon icon={FiClock} className="h-6 w-6 text-green-600" /></div>
                <h3 className="text-2xl font-bold text-gray-900">Business Hours</h3>
              </div>
              <div className="space-y-3">
                {officeHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <span className="font-medium text-gray-700">{schedule.day}</span>
                    <span className="text-gray-600">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Contact</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="bg-blue-100 p-2 rounded-full"><SafeIcon icon={FiMail} className="h-5 w-5 text-blue-600" /></div>
                  <div>
                    <p className="font-medium text-gray-900">Email Us</p>
                    <a href="mailto:johnny@minipore.com" className="text-blue-600 hover:text-blue-700">johnny@minipore.com</a>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="bg-green-100 p-2 rounded-full"><SafeIcon icon={FiPhone} className="h-5 w-5 text-green-600" /></div>
                  <div>
                    <p className="font-medium text-gray-900">Call Us</p>
                    <a href="tel:+8613122277752" className="text-green-600 hover:text-green-700">+86 131 2227 7752</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Need Technical Support?</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Product specifications and compatibility</li>
                <li>• Installation and maintenance guidance</li>
                <li>• Custom solution design</li>
                <li>• Troubleshooting and support</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;